import { UploadOutlined } from '@ant-design/icons';
import { ImageAddIcon, RippleIcon } from '@marine/icons';
import { Button, Spin } from 'antd';
import React, { FC, Suspense, useCallback, useMemo, useState } from 'react';
import { FileCard, GetFilesDataItem } from 'services';
import { Gallery } from '../FilesGallery/Gallery.component';
import styles from './UserBoatGallery.module.scss';
import { UserBoatGalleryProps } from './UserBoatGallery.props';

const FilePicker = React.lazy(() => import('../FilePicker'));

const UserBoatGallery: FC<UserBoatGalleryProps> = ({
  boatImages,
  setBoatImages = (_value: any) => {},
  onViewAll,
  readOnly,
  onChange = (_value: any) => {},
  value,
  disabled = false,
}) => {
  const [filePickerVisible, setFilePickerVisible] = useState(false);
  const initialValues = useMemo(() => value || boatImages, [value, boatImages]);

  const handleOnOkay = useCallback(
    (items: FileCard[]) => {
      setBoatImages(items as UserBoatGalleryProps['boatImages']);
      onChange(items as UserBoatGalleryProps['boatImages']);
    },
    [setBoatImages, onChange],
  );

  const _handleOnDeletedFiles = useCallback(
    (deletedFileIds: string[]) => {
      setBoatImages(boatImages?.filter((e) => e && !deletedFileIds.includes(e.id)));
      onChange(boatImages?.filter((e) => e && !deletedFileIds.includes(e.id)));
    },
    [boatImages, setBoatImages, onChange],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          Gallery
          {initialValues && initialValues?.length > 0 ? ` (${initialValues?.length})` : ''}
        </h3>

        <p className={styles.description}>
          Photos of your boat (e.g. interior, exterior, aerial), schematics (e.g. floor-plan, side-view), etc.
        </p>
      </div>

      <div>
        {initialValues && initialValues.length > 0 ? (
          <Gallery files={initialValues} />
        ) : (
          <div className={styles.galleryEmptyState}>
            <RippleIcon icon={<ImageAddIcon />} />

            <h3 className={styles.emptyTitle}>No images added yet...</h3>
            <p className={styles.emptyDescription}>Click ‘Upload’ to start adding your images...</p>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        {!readOnly && (
          <Button
            disabled={disabled}
            onClick={() => {
              setFilePickerVisible(true);
            }}
            icon={<UploadOutlined />}
          >
            Add files
          </Button>
        )}

        {onViewAll && initialValues && initialValues.length > 1 ? (
          <Button type="link" onClick={onViewAll} className={styles.viewAll}>
            View all ({initialValues?.length})
          </Button>
        ) : null}
      </div>

      {filePickerVisible && (
        <Suspense fallback={<Spin spinning fullscreen />}>
          <FilePicker
            title="Change gallery files"
            multiple
            initialSelectedFiles={(initialValues as GetFilesDataItem[]) || []}
            close={() => setFilePickerVisible(false)}
            onFinish={handleOnOkay}
            open={filePickerVisible}
          />
        </Suspense>
      )}
    </div>
  );
};

export default UserBoatGallery;
